import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Bell, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNotificationsPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    title: '', message: '', type: 'announcement', target_audience: 'all'
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API}/admin/notifications`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) setNotifications(await res.json());
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const sendNotification = async () => {
    try {
      const res = await fetch(`${API}/admin/notifications`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { toast.success('Notification sent'); setShowModal(false); fetchNotifications(); }
    } catch (error) { toast.error('Failed'); }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Notifications</h1>
            <p className={`${theme.textMuted} mt-1`}>Send notifications to users</p>
          </div>
          {hasRole(['super_admin', 'manager']) && (
            <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Send Notification
            </Button>
          )}
        </div>

        <Card className={theme.card}>
          <CardHeader><CardTitle className={theme.text}>Sent Notifications</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
            ) : notifications.length === 0 ? (
              <p className={`text-center py-8 ${theme.textMuted}`}>No notifications sent</p>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif) => (
                  <div key={notif.notification_id} className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Bell className={`h-4 w-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                          <span className={`font-medium ${theme.text}`}>{notif.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-200 text-gray-600'}`}>{notif.type}</span>
                        </div>
                        <p className={`text-sm ${theme.textMuted}`}>{notif.message}</p>
                        <p className={`text-xs mt-2 ${theme.textSubtle}`}>
                          Sent to: {notif.target_audience} • {new Date(notif.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader><CardTitle className={theme.text}>Send Notification</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className={theme.text}>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} className={theme.input} placeholder="Important Update" />
                </div>
                <div>
                  <Label className={theme.text}>Message</Label>
                  <textarea value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} className={`w-full h-24 px-3 py-2 rounded-md border resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`} placeholder="Your message..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Type</Label>
                    <select value={form.type} onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      <option value="announcement">Announcement</option>
                      <option value="promotion">Promotion</option>
                      <option value="reminder">Reminder</option>
                      <option value="alert">Alert</option>
                    </select>
                  </div>
                  <div>
                    <Label className={theme.text}>Audience</Label>
                    <select value={form.target_audience} onChange={(e) => setForm(p => ({ ...p, target_audience: e.target.value }))} className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                      <option value="all">All Users</option>
                      <option value="members">Members Only</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={sendNotification}><Send className="h-4 w-4 mr-1" /> Send</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNotificationsPage;
